// plp-inline-banners.js
// Collection PLP inline banners from Shopify metaobjects.
// Loaded on collection templates before theme-search-spring-plp.js.

(function (window) {
    function escapeHtml(value) {
        return String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    function sanitizeBannerUrl(url) {
        const trimmed = String(url || '').trim();
        if (!trimmed || /^\s*javascript:/i.test(trimmed)) return '#';
        return trimmed;
    }

    function getConfiguredProductsPerPage() {
        const section = document.querySelector('[data-products-per-page]');
        if (!section) return 0;
        const parsed = parseInt(section.dataset.productsPerPage, 10);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    function getColor(value, fallback = 'white') {
        const raw = String(value || '').toLowerCase().trim();
        if (raw === 'black' || raw === 'dark' || raw === 'primary') return 'black';
        if (raw === 'white' || raw === 'light') return 'white';
        return fallback;
    }

    function getAlignment(value) {
        const raw = String(value || '').toLowerCase().trim();
        if (raw === 'left' || raw === 'start') return 'left';
        if (raw === 'right' || raw === 'end') return 'right';
        return 'center';
    }

    function getBanners() {
        if (Array.isArray(getBanners._cache)) {
            return getBanners._cache;
        }

        try {
            const script = document.getElementById('plp-inline-banners-data');
            if (!script) return [];

            const raw = (script.textContent || '').trim();
            if (!raw || raw === '[]') {
                getBanners._cache = [];
                return getBanners._cache;
            }

            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed)) {
                console.log('PLP inline banners skipped: unexpected data shape');
                getBanners._cache = [];
                return getBanners._cache;
            }

            getBanners._cache = parsed
                .map((banner, index) => {
                    try {
                        const position = Number.parseInt(banner?.position, 10);
                        const image = typeof banner?.image === 'string' ? banner.image.trim() : '';
                        if (!image || !Number.isFinite(position) || position < 1) {
                            return null;
                        }

                        return {
                            position,
                            title: banner?.title || '',
                            sub_copy: banner?.sub_copy || '',
                            cta_text: banner?.cta_text || '',
                            cta_url: banner?.cta_url || '',
                            button_style: banner?.button_style || 'white',
                            text_color: banner?.text_color || banner?.text_colour || 'white',
                            content_alignment: banner?.content_alignment || banner?.text_alignment || banner?.alignment || 'center',
                            image,
                            image_srcset: banner?.image_srcset || '',
                            image_alt: banner?.image_alt || ''
                        };
                    } catch (e) {
                        console.log('PLP inline banner skipped at index', index, e);
                        return null;
                    }
                })
                .filter(Boolean)
                .sort((a, b) => a.position - b.position);
        } catch (e) {
            console.log('PLP inline banners skipped', e);
            getBanners._cache = [];
        }

        return getBanners._cache;
    }

    function renderBanner(banner, index) {
        try {
            if (!banner?.image) return '';

            const href = escapeHtml(sanitizeBannerUrl(banner.cta_url));
            const title = escapeHtml(banner.title);
            const subCopy = escapeHtml(banner.sub_copy);
            const cta = escapeHtml(banner.cta_text);
            const alt = escapeHtml(banner.image_alt || banner.title || banner.cta_text || '');
            const src = escapeHtml(banner.image);
            const srcset = banner.image_srcset ? `srcset="${escapeHtml(banner.image_srcset)}"` : '';
            const aspect = window.SearchSpringCommon?.PRODUCT_IMAGE_ASPECT_RATIO || 0.6671428571428571;
            const buttonStyle = getColor(banner.button_style, 'white');
            const textColor = getColor(banner.text_color, 'white');
            const alignment = getAlignment(banner.content_alignment);
            const buttonClass = buttonStyle === 'black'
                ? 'button button--primary plp-inline-banner__cta plp-inline-banner__cta--black'
                : 'button plp-inline-banner__cta plp-inline-banner__cta--white';

            const contentHtml = [title, subCopy, cta].some(Boolean) ? `
                <div class="plp-inline-banner__content plp-inline-banner__content--${textColor} plp-inline-banner__content--align-${alignment}">
                    ${title ? `<p class="plp-inline-banner__title heading heading--small">${title}</p>` : ''}
                    ${subCopy ? `<p class="plp-inline-banner__subcopy">${subCopy}</p>` : ''}
                    ${cta ? `<span class="${buttonClass}">${cta}</span>` : ''}
                </div>
            ` : '';

            return `
                <article class="product-item plp-inline-banner fade-in" data-plp-inline-banner="${index}">
                    <a class="plp-inline-banner__link" href="${href}">
                        <div class="product-item__image-wrapper">
                            <div class="product-item__aspect-ratio aspect-ratio" style="--aspect-ratio: ${aspect}">
                                <img class="plp-inline-banner__image product-item__primary-image" src="${src}" ${srcset} sizes="(max-width: 740px) 50vw, (max-width: 999px) 33vw, 25vw" alt="${alt}" loading="lazy">
                                ${contentHtml}
                            </div>
                        </div>
                    </a>
                </article>
            `;
        } catch (e) {
            console.log('PLP inline banner skipped at position', banner?.position, e);
            return '';
        }
    }

    function getGridColumns() {
        const section = document.querySelector('[data-desktop-products-per-row]');
        const parsed = parseInt(section?.dataset.desktopProductsPerRow, 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 4;
    }

    function getPageWindow(page) {
        const pageSize = getConfiguredProductsPerPage();
        const parsedPage = Number.parseInt(page, 10);
        const safePage = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1;
        const pageStart = pageSize > 0 ? (safePage - 1) * pageSize + 1 : 1;
        const pageEnd = pageSize > 0 ? safePage * pageSize : Number.POSITIVE_INFINITY;
        return { pageStart, pageEnd, pageSize };
    }

    function productsForPage(pageSize, bannerCount, columns) {
        let count = pageSize;
        if (columns > 1 && count > 0) {
            const remainder = (count + bannerCount) % columns;
            if (remainder) count += columns - remainder;
        }
        return count;
    }

    function getProductWindow(page) {
        const columns = getGridColumns();
        const banners = getBanners();
        const parsedPage = Number.parseInt(page, 10);
        const safePage = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1;
        let offset = 0;

        for (let current = 1; current <= safePage; current += 1) {
            const { pageStart, pageEnd, pageSize } = getPageWindow(current);
            const bannerCount = banners.filter((banner) => banner.position >= pageStart && banner.position <= pageEnd).length;
            const count = productsForPage(pageSize, bannerCount, columns);
            if (current === safePage) {
                return { offset, count, pageStart, pageEnd };
            }
            offset += count;
        }

        return { offset: 0, count: getConfiguredProductsPerPage(), pageStart: 1, pageEnd: getConfiguredProductsPerPage() };
    }

    function getDisplayPageCount(totalProducts) {
        const total = Number(totalProducts) || 0;
        const pageSize = getConfiguredProductsPerPage();
        if (total <= 0 || !pageSize) return 1;

        let offset = 0;
        let page = 0;
        while (offset < total && page < 1000) {
            page += 1;
            const plan = getProductWindow(page);
            const take = Math.min(plan.count, total - offset);
            if (take <= 0) break;
            offset += take;
        }
        return Math.max(page, 1);
    }

    function buildGridHtml(productResults, renderProduct, page) {
        try {
            const banners = getBanners();
            if (!banners.length) return null;

            const { pageStart, pageEnd } = getPageWindow(page);
            const remaining = banners.filter((banner) => banner.position >= pageStart && banner.position <= pageEnd);
            if (!remaining.length) return null;

            let html = '';
            let slot = pageStart;

            productResults.forEach((product, index) => {
                while (remaining.length && remaining[0].position <= slot) {
                    const banner = remaining.shift();
                    const bannerHtml = renderBanner(banner, banners.indexOf(banner));
                    if (!bannerHtml) {
                        console.log('PLP inline banner skipped at position', banner?.position);
                        continue;
                    }
                    html += bannerHtml;
                    slot += 1;
                }
                html += renderProduct(product, index);
                slot += 1;
            });

            remaining.forEach((banner) => {
                if (banner.position > slot) return;
                const bannerHtml = renderBanner(banner, banners.indexOf(banner));
                if (!bannerHtml) {
                    console.log('PLP inline banner skipped at position', banner?.position);
                    return;
                }
                html += bannerHtml;
            });

            return html;
        } catch (e) {
            console.log('PLP inline banners skipped', e);
            return null;
        }
    }

    window.PlpInlineBanners = {
        getBanners,
        getCount() {
            try {
                return getBanners().length;
            } catch (e) {
                console.log('PLP inline banners skipped', e);
                return 0;
            }
        },
        getProductWindow,
        getDisplayPageCount,
        buildGridHtml
    };
})(window);
